import { useCallback, useEffect, useRef, useState } from "react";

// Only one browser tab may run the softphone: DialStack delivers every ring
// leg of an inbound call to the user's most-recently-connected session, so a
// second connected tab silently swallows ringing in the first (APP-61) — and
// answering the silent leg strands the call. A Web-Locks election lets exactly
// one tab (the leader) mint a session and connect; the others stand by with a
// take-over affordance. This stays correct after DialStack fixes leg scoping:
// one connection per browser is the right shape regardless.
//
// Mechanics: an enabled tab first probes with `ifAvailable` (grants when free,
// hands back null when another tab holds the lock — no timers or polling),
// then keeps one queued plain request while standing by. The browser releases
// a dead tab's lock and grants the next queued request, so leader death hands
// off automatically. Take-over uses `steal`: the holder's request promise
// rejects, which that tab observes to drop its softphone and re-queue.
//
// A call cannot move with the phone: its media lives in the leader's peer
// connection, and the SDK has no hand-off, so stealing mid-call hangs up on
// the caller (ENG-2178). The leader therefore holds a second lock for as long
// as it has any call, and take-over refuses while that lock is held.
//
// Browsers without `navigator.locks` (and jsdom) keep the pre-election
// behavior: every tab is its own leader.

export const SOFTPHONE_LEADER_LOCK = "suno:dialstack:softphone-leader";
export const SOFTPHONE_ON_CALL_LOCK = "suno:dialstack:softphone-on-call";

// How often a standby tab looking at the take-over prompt re-checks whether
// the leader's call has ended.
export const ON_CALL_RECHECK_MS = 1_000;

export type SoftphoneTabLeader = {
  // This tab may mint a session and connect the softphone.
  isLeader: boolean;
  // Another tab holds the softphone; this one is queued behind it.
  blockedByOtherTab: boolean;
  // Steal leadership from the holding tab (it disconnects, this tab connects).
  // Resolves false, without stealing, while the holding tab is on a call.
  takeOver: () => Promise<boolean>;
};

type LeaderPhase = "pending" | "leader" | "standby";

const locksAvailable = () =>
  typeof navigator !== "undefined" && Boolean(navigator.locks);

// Only the leader ever takes the on-call lock, so from a standby tab "held"
// means the leader is on a call.
export async function isSoftphoneOnCallInAnotherTab(): Promise<boolean> {
  if (!locksAvailable()) return false;
  const { held = [] } = await navigator.locks.query();
  return held.some((lock) => lock.name === SOFTPHONE_ON_CALL_LOCK);
}

// Leader side: hold the on-call lock while this tab has any call — ringing,
// live or on hold. The browser drops it with the tab.
export function useSoftphoneOnCallLock(onCall: boolean) {
  useEffect(() => {
    if (!onCall || !locksAvailable()) return undefined;
    let ended = false;
    let release: (() => void) | null = null;
    const controller = new AbortController();
    navigator.locks
      .request(SOFTPHONE_ON_CALL_LOCK, { signal: controller.signal }, () =>
        ended
          ? undefined
          : new Promise<void>((resolve) => {
              release = resolve;
            })
      )
      .catch(() => undefined);
    return () => {
      ended = true;
      controller.abort();
      release?.();
    };
  }, [onCall]);
}

// Standby side: whether the leading tab is on a call, re-checked while
// `watching` (the take-over prompt is open) so the prompt unlocks when the
// call ends.
export function useSoftphoneOnCallInAnotherTab(watching: boolean): boolean {
  const [onCall, setOnCall] = useState(false);
  useEffect(() => {
    if (!watching) return undefined;
    let stopped = false;
    const check = () =>
      isSoftphoneOnCallInAnotherTab().then((value) => {
        if (!stopped) setOnCall(value);
      });
    check();
    const recheck = setInterval(check, ON_CALL_RECHECK_MS);
    return () => {
      stopped = true;
      clearInterval(recheck);
    };
  }, [watching]);
  return watching && onCall;
}

export function useSoftphoneTabLeader(enabled: boolean): SoftphoneTabLeader {
  const [phase, setPhase] = useState<LeaderPhase>("pending");
  // Resolving this releases the held lock (the grant callback's promise).
  const releaseRef = useRef<(() => void) | null>(null);
  // Aborts the queued (waiting) request, if any.
  const waitControllerRef = useRef<AbortController | null>(null);
  // Bumped to invalidate outstanding grant callbacks and catch handlers —
  // distinguishes "we cancelled our own request" from "another tab stole it".
  const requestTokenRef = useRef(0);
  const takeOverRef = useRef<() => Promise<boolean>>(async () => false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!locksAvailable()) {
      setPhase("leader");
      return () => setPhase("pending");
    }

    const invalidateOutstanding = () => {
      requestTokenRef.current += 1;
      waitControllerRef.current?.abort();
      waitControllerRef.current = null;
    };

    // Held for the lifetime of our leadership; resolving releases the lock.
    const holdLock = (token: number) =>
      new Promise<void>((resolve) => {
        if (token !== requestTokenRef.current) {
          resolve();
          return;
        }
        releaseRef.current = resolve;
        setPhase("leader");
      });

    // The lock was taken from us (steal) or the grant failed: surrender and
    // re-queue so this tab still inherits leadership if the holder goes away.
    const recover = (token: number) => () => {
      if (token !== requestTokenRef.current) return;
      releaseRef.current?.();
      releaseRef.current = null;
      setPhase("standby");
      enqueue();
    };

    function enqueue() {
      const token = requestTokenRef.current;
      const controller = new AbortController();
      waitControllerRef.current = controller;
      navigator.locks
        .request(SOFTPHONE_LEADER_LOCK, { signal: controller.signal }, () =>
          holdLock(token)
        )
        .catch(recover(token));
    }

    const probe = () => {
      const token = requestTokenRef.current;
      navigator.locks
        .request(SOFTPHONE_LEADER_LOCK, { ifAvailable: true }, (lock) => {
          if (lock === null) {
            if (token === requestTokenRef.current) {
              setPhase("standby");
              enqueue();
            }
            return undefined;
          }
          return holdLock(token);
        })
        .catch(recover(token));
    };

    let disposed = false;
    takeOverRef.current = async () => {
      if (releaseRef.current) return true; // already the leader
      if (await isSoftphoneOnCallInAnotherTab()) return false;
      if (disposed) return false;
      if (releaseRef.current) return true; // inherited it while checking
      invalidateOutstanding();
      const token = requestTokenRef.current;
      navigator.locks
        .request(SOFTPHONE_LEADER_LOCK, { steal: true }, () => holdLock(token))
        .catch(recover(token));
      return true;
    };

    invalidateOutstanding();
    probe();

    return () => {
      disposed = true;
      takeOverRef.current = async () => false;
      invalidateOutstanding();
      releaseRef.current?.();
      releaseRef.current = null;
      setPhase("pending");
    };
  }, [enabled]);

  const takeOver = useCallback(() => takeOverRef.current(), []);

  return {
    isLeader: enabled && phase === "leader",
    blockedByOtherTab: enabled && phase === "standby",
    takeOver
  };
}
